/* DRIVE IQ — ADVENTURE MODE V600.7 SAFE HYBRID VISUAL MODE
   File: adventure-map.js

   Sole Mapbox lifecycle and camera owner.

   V600.6:
   - Separates surface recovery from camera recovery.
   - Scan completion, shelf changes, UI transitions, and Place IQ close may repaint/resize the map but cannot restore an older saved camera.
   - Background/pageshow recovery may restore the saved camera only when explicitly requested.
   - Prevents repeated recovery timers from pulling city scans back to statewide or regional views.
   - Rejects unsafe oversized result bounds before fitBounds.
   - Falls back to a stable result-center camera when result geometry is too broad.

   Previous recovery foundation:
   - Coalesces restore/resize work so foreground callbacks cannot fight.
   - Preserves committed camera state across background/foreground recovery.
   - Safely queues camera work until the map/style is ready.
   - Keeps map gestures live without global touch interception.
   - Prevents stale animation callbacks from overriding newer selections.
   - Emits explicit interaction, camera, recovery, and lifecycle events.
   - Never renders pins, opens Place IQ, executes scans, or launches Drive Mode.
   - Force-restores the map container, canvas container, canvas, and WebGL surface visibility.
   - Repairs zero-height and zero-width map layouts before calling Mapbox resize.
   - Runs bounded recovery passes after scan completion, results-shell restore, and app foreground.
   - Reapplies the last safe camera without clearing or recreating the map.
   - Detects blank/hidden map surfaces and triggers repaint, resize, and camera recovery.
   - Keeps the existing map instance alive and never destroys it during recovery.
*/

(function(window,document){
"use strict";

if(window.DriveIQAdventureMap){return;}

var map=null;
var initialized=false;
var destroyed=false;
var generation=0;
var resizeTimer=null;
var restoreTimer=null;
var cameraTimer=null;
var pendingCamera=null;
var pendingFit=null;
var restoring=false;
var lastRestoreReason="";
var lastCameraCommitAt=0;
var CAMERA_COMMIT_DELAY=70;
var recoveryTimerIds=[];
var RECOVERY_DELAYS=[0,80,220,520,900];
var MIN_MAP_HEIGHT=240;
var lastHealthySurfaceAt=0;

/* Phase 1 visual-only basemap controller. Camera, scan, and result authority remain unchanged. */
var visualStyleMode="wide";
var styleSwitchInFlight=false;
var SATELLITE_ENTER_ZOOM=14.6;
var SATELLITE_EXIT_ZOOM=14.15;
var SATELLITE_STYLE_URL="mapbox://styles/mapbox/satellite-streets-v12";

function cfg(){
  return window.DriveIQAdventureConfig||{};
}

function state(){
  return window.DriveIQAdventureState||null;
}

function events(){
  return window.DriveIQAdventureEvents||null;
}

function numberValue(value,fallback){
  var parsed=Number(value);
  return isFinite(parsed)?parsed:Number(fallback);
}

function booleanValue(value,fallback){
  return typeof value==="boolean"?value:fallback;
}

function clonePlain(value){
  if(!value||typeof value!=="object"){return value;}

  try{
    return JSON.parse(JSON.stringify(value));
  }catch(error){
    return value;
  }
}

function interactionConfig(){
  var mapConfigValue=cfg().map||{};
  var interaction=mapConfigValue.interaction||{};

  return {
    dragPan:booleanValue(interaction.dragPan,true),
    scrollZoom:booleanValue(interaction.scrollZoom,true),
    touchZoomRotate:booleanValue(interaction.touchZoomRotate,true),
    touchPitch:booleanValue(interaction.touchPitch,false),
    doubleClickZoom:booleanValue(interaction.doubleClickZoom,true),
    boxZoom:booleanValue(interaction.boxZoom,false),
    keyboard:booleanValue(interaction.keyboard,true)
  };
}

function mapConfig(){
  var config=cfg();
  var value=config.map||{};
  var fallback=value.fallbackCenter||{};
  var padding=value.fitPadding||{};

  return {
    containerID:String(value.containerID||"adventureMap"),
    styleURL:String(value.styleURL||"mapbox://styles/mapbox/navigation-night-v1"),
    fallbackCenter:{
      lat:numberValue(fallback.lat,34.98898),
      lng:numberValue(fallback.lng,-90.01259)
    },
    initialZoom:numberValue(value.initialZoom,11.5),
    resultZoom:numberValue(value.resultZoom,13.2),
    placeZoom:numberValue(value.placeZoom,15.2),
    minZoom:numberValue(value.minZoom,2),
    maxZoom:numberValue(value.maxZoom,20),
    defaultBearing:numberValue(value.defaultBearing,0),
    defaultPitch:numberValue(value.defaultPitch,0),
    fitPadding:{
      top:numberValue(padding.top,150),
      right:numberValue(padding.right,70),
      bottom:numberValue(padding.bottom,245),
      left:numberValue(padding.left,70)
    },
    interaction:interactionConfig()
  };
}

function validCoordinate(value){
  var lat;
  var lng;

  if(!value){return null;}

  if(Array.isArray(value)&&value.length>=2){
    lng=Number(value[0]);
    lat=Number(value[1]);
  }else{
    lat=Number(
      value.lat!==undefined
        ? value.lat
        : value.latitude
    );

    lng=Number(
      value.lng!==undefined
        ? value.lng
        : value.lon!==undefined
          ? value.lon
          : value.longitude
    );
  }

  if(
    !isFinite(lat)||
    !isFinite(lng)||
    Math.abs(lat)>90||
    Math.abs(lng)>180
  ){
    return null;
  }

  return {lat:lat,lng:lng};
}

function stateGet(path,fallback){
  var appState=state();

  if(appState&&typeof appState.get==="function"){
    try{
      return appState.get(path,fallback);
    }catch(error){}
  }

  return fallback;
}

function currentCenter(){
  var destination=
    stateGet("destination.committed",null)||
    stateGet("destination.selected",null);
  var lastFix=stateGet("location.lastFix",null);
  var savedCamera=stateGet("map.camera",null);
  var config=mapConfig();

  return validCoordinate(destination)||
    validCoordinate(savedCamera&&savedCamera.center)||
    validCoordinate(lastFix)||
    config.fallbackCenter;
}

function emit(name,payload){
  var bus=events();

  if(bus&&typeof bus.emit==="function"){
    try{
      bus.emit(name,payload||{});
    }catch(error){}
  }
}

function mergeState(path,value,action){
  var appState=state();

  if(appState&&typeof appState.merge==="function"){
    try{
      appState.merge(path,clonePlain(value),{
        action:action||"map:update"
      });
    }catch(error){}
  }
}

function setState(path,value,action){
  var appState=state();

  if(appState&&typeof appState.set==="function"){
    try{
      appState.set(path,clonePlain(value),{
        action:action||"map:set"
      });
      return;
    }catch(error){}
  }

  mergeState(path,value,action);
}

function enableHandler(handler,enabled){
  if(!handler){return;}

  try{
    if(enabled&&typeof handler.enable==="function"){
      handler.enable();
    }else if(!enabled&&typeof handler.disable==="function"){
      handler.disable();
    }
  }catch(error){}
}

function mapElements(){
  var config=mapConfig();
  var container=null;
  var canvasContainer=null;
  var canvas=null;

  try{
    container=map&&typeof map.getContainer==="function"
      ? map.getContainer()
      : document.getElementById(config.containerID);
  }catch(error){
    container=document.getElementById(config.containerID);
  }

  if(container){
    canvasContainer=container.querySelector(
      ".mapboxgl-canvas-container"
    );
    canvas=container.querySelector(
      ".mapboxgl-canvas"
    );
  }

  return {
    container:container,
    canvasContainer:canvasContainer,
    canvas:canvas
  };
}

function forceSurfaceVisible(reason){
  var elements=mapElements();
  var container=elements.container;
  var canvasContainer=elements.canvasContainer;
  var canvas=elements.canvas;
  var viewportHeight=Math.max(
    MIN_MAP_HEIGHT,
    Number(window.innerHeight||0)
  );
  var rect;

  if(!container){
    emit("map:warning",{
      code:"MAP_SURFACE_CONTAINER_MISSING",
      reason:String(reason||"surface-recovery")
    });
    return false;
  }

  [
    container,
    canvasContainer,
    canvas
  ].forEach(function(node){
    if(!node){return;}

    node.style.setProperty("display","block","important");
    node.style.setProperty("visibility","visible","important");
    node.style.setProperty("opacity","1","important");
  });

  container.style.setProperty("position","absolute","important");
  container.style.setProperty("inset","0","important");
  container.style.setProperty("width","100%","important");
  container.style.setProperty(
    "min-height",
    String(viewportHeight)+"px",
    "important"
  );
  container.style.setProperty("background","#07111c","important");

  try{
    rect=container.getBoundingClientRect();
  }catch(error){
    rect=null;
  }

  if(
    !rect||
    rect.width<2||
    rect.height<MIN_MAP_HEIGHT
  ){
    container.style.setProperty(
      "height",
      String(viewportHeight)+"px",
      "important"
    );
  }else{
    container.style.setProperty(
      "height",
      "100%",
      "important"
    );
  }

  if(canvasContainer){
    canvasContainer.style.setProperty("width","100%","important");
    canvasContainer.style.setProperty("height","100%","important");
  }

  if(canvas){
    canvas.style.setProperty("width","100%","important");
    canvas.style.setProperty("height","100%","important");
  }

  mergeState("map",{
    containerReady:true,
    surfaceVisible:true,
    lastSurfaceRecoveryReason:String(
      reason||"surface-recovery"
    ),
    lastSurfaceRecoveryAt:Date.now()
  },"map:surface-visible");

  return true;
}

function surfaceLooksHealthy(){
  var elements=mapElements();
  var container=elements.container;
  var canvas=elements.canvas;
  var rect;

  if(!container||!canvas){
    return false;
  }

  try{
    rect=container.getBoundingClientRect();
  }catch(error){
    return false;
  }

  return !!(
    rect.width>2&&
    rect.height>=MIN_MAP_HEIGHT&&
    window.getComputedStyle(container).display!=="none"&&
    window.getComputedStyle(container).visibility!=="hidden"&&
    window.getComputedStyle(canvas).display!=="none"&&
    window.getComputedStyle(canvas).visibility!=="hidden"
  );
}

function clearRecoveryTimers(){
  recoveryTimerIds.forEach(function(timerId){
    window.clearTimeout(timerId);
  });

  recoveryTimerIds.length=0;
  return true;
}

function recoverSurface(reason,options){
  var supplied=options||{};
  var requestedReason=String(reason||"recover-surface");
  var saved=stateGet("map.camera",null);
  var cameraMode=String(
    supplied.cameraMode||
    "surface-only"
  );
  var safeCenter=cameraMode==="restore-saved"
    ? validCoordinate(
        supplied.center||
        saved&&saved.center||
        currentCenter()
      )
    : validCoordinate(supplied.center);

  if(!map){
    return false;
  }

  forceSurfaceVisible(requestedReason);
  applyInteraction(requestedReason);
  resize(requestedReason);

  try{
    map.triggerRepaint();
  }catch(error){}

  /*
    V600.6:
    Most UI and scan recovery events only need resize/repaint. They must not
    jump back to an older map.camera snapshot after fresh results were fit.
    Camera movement is allowed only when the caller explicitly supplies a
    center or requests restore-saved.
  */
  if(safeCenter){
    try{
      map.jumpTo({
        center:[safeCenter.lng,safeCenter.lat],
        zoom:numberValue(
          supplied.zoom,
          cameraMode==="restore-saved"&&
          saved&&saved.zoom!==undefined
            ? saved.zoom
            : map.getZoom()
        ),
        bearing:numberValue(
          cameraMode==="restore-saved"&&saved&&saved.bearing,
          map.getBearing()
        ),
        pitch:numberValue(
          cameraMode==="restore-saved"&&saved&&saved.pitch,
          map.getPitch()
        )
      });
    }catch(error){}
  }

  window.requestAnimationFrame(function(){
    if(!map){return;}

    forceSurfaceVisible(
      requestedReason+"-raf"
    );
    resize(
      requestedReason+"-raf"
    );

    try{
      map.triggerRepaint();
    }catch(error){}

    flushPendingCamera();
    flushPendingFit();

    if(surfaceLooksHealthy()){
      lastHealthySurfaceAt=Date.now();
    }

    mergeState("map",{
      recovering:false,
      surfaceVisible:surfaceLooksHealthy(),
      lastHealthySurfaceAt:lastHealthySurfaceAt
    },"map:surface-recovered");

    emit("map:surface-recovered",{
      reason:requestedReason,
      healthy:surfaceLooksHealthy(),
      camera:cameraSnapshot()
    });
  });

  return true;
}

function scheduleSurfaceRecovery(reason,options){
  var supplied=options||{};

  clearRecoveryTimers();

  RECOVERY_DELAYS.forEach(function(delay){
    recoveryTimerIds.push(
      window.setTimeout(function(){
        recoverSurface(
          String(reason||"scheduled-recovery")+
          "-"+String(delay),
          supplied
        );
      },delay)
    );
  });

  return true;
}

function updatePointerSurface(enabled){
  var container;
  var canvasContainer;
  var canvas;

  if(!map){return;}

  try{
    container=map.getContainer();
    canvasContainer=container.querySelector(".mapboxgl-canvas-container");
    canvas=container.querySelector(".mapboxgl-canvas");

    container.style.pointerEvents=enabled?"auto":"none";
    container.style.touchAction=enabled?"none":"auto";

    if(canvasContainer){
      canvasContainer.style.pointerEvents=enabled?"auto":"none";
      canvasContainer.style.touchAction=enabled?"none":"auto";
    }

    if(canvas){
      canvas.style.pointerEvents=enabled?"auto":"none";
      canvas.style.touchAction=enabled?"none":"auto";
    }
  }catch(error){}
}

function setInteraction(enabled,reason){
  var interaction=interactionConfig();

  if(!map){return false;}

  enableHandler(map.dragPan,enabled&&interaction.dragPan);
  enableHandler(map.scrollZoom,enabled&&interaction.scrollZoom);
  enableHandler(map.touchZoomRotate,enabled&&interaction.touchZoomRotate);
  enableHandler(map.touchPitch,enabled&&interaction.touchPitch);
  enableHandler(map.doubleClickZoom,enabled&&interaction.doubleClickZoom);
  enableHandler(map.boxZoom,enabled&&interaction.boxZoom);
  enableHandler(map.keyboard,enabled&&interaction.keyboard);

  updatePointerSurface(enabled);

  mergeState("map",{
    interactionEnabled:!!enabled
  },enabled?"map:interaction-enabled":"map:interaction-disabled");

  emit(
    enabled?"map:interaction-enabled":"map:interaction-disabled",
    {reason:String(reason||"manual")}
  );

  return true;
}

function applyInteraction(reason){
  return setInteraction(true,reason||"apply");
}

function cameraSnapshot(){
  var center;

  if(!map){return null;}

  try{
    center=map.getCenter();

    return {
      center:{
        lat:Number(center.lat),
        lng:Number(center.lng)
      },
      zoom:Number(map.getZoom()),
      bearing:Number(map.getBearing()),
      pitch:Number(map.getPitch())
    };
  }catch(error){
    return null;
  }
}

function commitCamera(action){
  var snapshot=cameraSnapshot();

  if(!snapshot){return null;}

  lastCameraCommitAt=Date.now();
  setState("map.camera",snapshot,action||"map:camera");
  return snapshot;
}

function scheduleCameraCommit(action){
  if(cameraTimer){
    window.clearTimeout(cameraTimer);
  }

  cameraTimer=window.setTimeout(function(){
    cameraTimer=null;
    commitCamera(action||"map:camera-settled");
  },CAMERA_COMMIT_DELAY);
}

function mapLoaded(){
  return !!(
    map&&
    typeof map.loaded==="function"&&
    map.loaded()
  );
}

function styleLoaded(){
  if(!map){return false;}

  try{
    return typeof map.isStyleLoaded!=="function"||map.isStyleLoaded();
  }catch(error){
    return false;
  }
}

function readyForCamera(){
  return !!(map&&mapLoaded()&&styleLoaded());
}

function sanitizeCameraOptions(options){
  var next=options||{};
  var center=validCoordinate(next.center);
  var payload={};

  if(center){payload.center=center;}
  if(next.zoom!==undefined&&isFinite(Number(next.zoom))){
    payload.zoom=Number(next.zoom);
  }
  if(next.bearing!==undefined&&isFinite(Number(next.bearing))){
    payload.bearing=Number(next.bearing);
  }
  if(next.pitch!==undefined&&isFinite(Number(next.pitch))){
    payload.pitch=Number(next.pitch);
  }
  if(next.duration!==undefined&&isFinite(Number(next.duration))){
    payload.duration=Math.max(0,Number(next.duration));
  }
  if(Array.isArray(next.offset)&&next.offset.length>=2){
    payload.offset=[
      numberValue(next.offset[0],0),
      numberValue(next.offset[1],0)
    ];
  }
  if(next.essential!==undefined){
    payload.essential=!!next.essential;
  }

  return payload;
}

function queueCamera(kind,options){
  pendingCamera={
    kind:String(kind||"ease"),
    options:sanitizeCameraOptions(options),
    generation:generation
  };

  return true;
}

function flushPendingCamera(){
  var pending;

  if(!readyForCamera()||!pendingCamera){return false;}

  pending=pendingCamera;
  pendingCamera=null;

  if(pending.generation!==generation){return false;}

  if(pending.kind==="jump"){
    return jumpTo(pending.options,true);
  }

  if(pending.kind==="fly"){
    return flyTo(pending.options,true);
  }

  return easeTo(pending.options,true);
}

function flushPendingFit(){
  var pending;

  if(!readyForCamera()||!pendingFit){return false;}

  pending=pendingFit;
  pendingFit=null;

  if(pending.generation!==generation){return false;}

  return fitResults(pending.items,pending.options,true);
}

function wideStyleURL(){
  return mapConfig().styleURL||"mapbox://styles/mapbox/navigation-night-v1";
}

function targetVisualStyleMode(zoom){
  var z=numberValue(zoom,mapConfig().initialZoom);

  if(visualStyleMode==="satellite"){
    return z<=SATELLITE_EXIT_ZOOM?"wide":"satellite";
  }

  return z>=SATELLITE_ENTER_ZOOM?"satellite":"wide";
}

function applyVisualStyleMode(nextMode,reason){
  var requested=String(nextMode||"wide")==="satellite"?"satellite":"wide";
  var styleURL=requested==="satellite"?SATELLITE_STYLE_URL:wideStyleURL();
  var snapshot;

  if(!map||styleSwitchInFlight||requested===visualStyleMode){return false;}
  if(!mapLoaded()||!styleLoaded()){return false;}

  snapshot=cameraSnapshot();
  styleSwitchInFlight=true;
  visualStyleMode=requested;

  try{
    if(document&&document.body){
      document.body.classList.toggle("diq-adventure-satellite-mode",requested==="satellite");
    }

    mergeState("map",{
      visualStyleMode:requested,
      visualStyleReason:String(reason||"zoom"),
      visualStyleChangedAt:Date.now(),
      cameraBeforeStyle:snapshot
    },"map:visual-style");

    emit("map:visual-style-changing",{
      mode:requested,
      reason:String(reason||"zoom"),
      camera:snapshot
    });

    /* Mapbox setStyle preserves camera. Pins already restore themselves on style-ready. */
    map.setStyle(styleURL,{diff:true});
    return true;
  }catch(error){
    styleSwitchInFlight=false;
    emit("map:warning",{
      code:"VISUAL_STYLE_SWITCH_FAILED",
      mode:requested,
      message:String(error&&error.message?error.message:error||"")
    });
    return false;
  }
}

function syncVisualStyle(reason){
  var zoom;
  var target;

  if(!map||styleSwitchInFlight){return false;}

  try{zoom=Number(map.getZoom());}catch(error){return false;}
  target=targetVisualStyleMode(zoom);

  if(target===visualStyleMode){
    if(document&&document.body){
      document.body.classList.toggle("diq-adventure-satellite-mode",target==="satellite");
    }
    return false;
  }

  return applyVisualStyleMode(target,reason||"zoom");
}

function init(options){
  var config=mapConfig();
  var savedCamera=stateGet("map.camera",null);
  var requestedCenter=validCoordinate(options&&options.center);
  var center=requestedCenter||
    validCoordinate(savedCamera&&savedCamera.center)||
    currentCenter();
  var token=(cfg().api||{}).mapboxToken||
    window.DRIVE_IQ_MAPBOX_TOKEN||
    "";
  var localGeneration;

  if(map&&!destroyed){
    restore("init-existing");
    return map;
  }

  if(
    !window.mapboxgl||
    typeof window.mapboxgl.Map!=="function"
  ){
    emit("map:error",{
      code:"MAPBOX_NOT_LOADED",
      message:"Mapbox GL JS is not loaded."
    });
    return null;
  }

  if(!document.getElementById(config.containerID)){
    emit("map:error",{
      code:"MAP_CONTAINER_MISSING",
      message:"Map container #"+config.containerID+" was not found."
    });
    return null;
  }

  if(!token){
    emit("map:error",{
      code:"MAPBOX_TOKEN_MISSING",
      message:"Mapbox token is missing."
    });
    return null;
  }

  generation+=1;
  localGeneration=generation;
  window.mapboxgl.accessToken=token;

  try{
    map=new window.mapboxgl.Map({
      container:config.containerID,
      style:config.styleURL,
      center:[center.lng,center.lat],
      zoom:numberValue(
        options&&options.zoom,
        savedCamera&&savedCamera.zoom!==undefined
          ? savedCamera.zoom
          : config.initialZoom
      ),
      minZoom:config.minZoom,
      maxZoom:config.maxZoom,
      bearing:numberValue(
        options&&options.bearing,
        savedCamera&&savedCamera.bearing!==undefined
          ? savedCamera.bearing
          : config.defaultBearing
      ),
      pitch:numberValue(
        options&&options.pitch,
        savedCamera&&savedCamera.pitch!==undefined
          ? savedCamera.pitch
          : config.defaultPitch
      ),
      attributionControl:true,
      interactive:true,
      preserveDrawingBuffer:false,
      trackResize:true,
      renderWorldCopies:false,
      cooperativeGestures:false,
      dragRotate:false,
      touchPitch:config.interaction.touchPitch
    });
  }catch(error){
    emit("map:error",{
      code:"MAP_CREATE_FAILED",
      message:String(
        error&&error.message
          ? error.message
          : error||"Map creation failed"
      )
    });
    map=null;
    return null;
  }

  initialized=true;
  destroyed=false;

  mergeState("map",{
    loaded:false,
    styleLoaded:false,
    interactionEnabled:false,
    containerReady:true,
    recovering:false,
    generation:localGeneration
  },"map:init");

  map.on("load",function(){
    if(localGeneration!==generation||!map){return;}

    mergeState("map",{
      loaded:true,
      styleLoaded:styleLoaded(),
      containerReady:true
    },"map:load");

    forceSurfaceVisible("load");
    applyInteraction("load");
    resize("load");
    flushPendingCamera();
    flushPendingFit();
    commitCamera("map:load-camera");
    emit("map:ready",{map:map,generation:localGeneration});
    window.setTimeout(function(){syncVisualStyle("initial-load");},0);
  });

  map.on("style.load",function(){
    if(localGeneration!==generation||!map){return;}

    styleSwitchInFlight=false;

    mergeState("map",{
      styleLoaded:true
    },"map:style-load");

    forceSurfaceVisible("style-load");
    applyInteraction("style-load");
    resize("style-load");
    flushPendingCamera();
    flushPendingFit();
    emit("map:style-ready",{map:map,generation:localGeneration,visualStyleMode:visualStyleMode});
    emit("map:visual-style-ready",{mode:visualStyleMode,camera:cameraSnapshot()});
  });

  map.on("movestart",function(){
    if(localGeneration!==generation){return;}
    emit("map:camera-moving",{camera:cameraSnapshot()});
  });

  map.on("moveend",function(){
    var snapshot;

    if(localGeneration!==generation){return;}

    snapshot=commitCamera("map:camera-changed");
    emit("map:camera-changed",{camera:snapshot});
    syncVisualStyle("moveend");
  });

  map.on("idle",function(){
    if(localGeneration!==generation){return;}
    flushPendingCamera();
    flushPendingFit();
    scheduleCameraCommit("map:idle-camera");
    emit("map:idle",{camera:cameraSnapshot()});
  });

  map.on("error",function(event){
    if(localGeneration!==generation){return;}

    emit("map:error",{
      code:"MAPBOX_RUNTIME_ERROR",
      message:String(
        event&&event.error&&event.error.message
          ? event.error.message
          : "Mapbox emitted an error."
      )
    });
  });

  return map;
}

function getMap(){
  return map;
}

function isReady(){
  return readyForCamera();
}

function resize(reason){
  if(!map){return false;}

  forceSurfaceVisible(
    String(reason||"manual")+"-resize"
  );

  try{
    map.resize();

    try{
      map.triggerRepaint();
    }catch(repaintError){}

    if(surfaceLooksHealthy()){
      lastHealthySurfaceAt=Date.now();
    }

    emit("map:resized",{
      reason:String(reason||"manual"),
      healthy:surfaceLooksHealthy()
    });
    return true;
  }catch(error){
    emit("map:warning",{
      code:"MAP_RESIZE_FAILED",
      reason:String(reason||"manual")
    });
    return false;
  }
}

function scheduleResize(reason,delay){
  if(resizeTimer){
    window.clearTimeout(resizeTimer);
  }

  resizeTimer=window.setTimeout(function(){
    resizeTimer=null;
    resize(reason||"scheduled");
  },numberValue(delay,80));

  return true;
}

function jumpTo(options,fromQueue){
  var next=sanitizeCameraOptions(options);
  var payload={};

  if(!map){return false;}
  if(!readyForCamera()&&!fromQueue){
    return queueCamera("jump",next);
  }

  if(next.center){
    payload.center=[next.center.lng,next.center.lat];
  }
  if(next.zoom!==undefined){payload.zoom=next.zoom;}
  if(next.bearing!==undefined){payload.bearing=next.bearing;}
  if(next.pitch!==undefined){payload.pitch=next.pitch;}

  try{
    map.stop();
    map.jumpTo(payload);
    scheduleCameraCommit("map:jump");
    return true;
  }catch(error){
    return false;
  }
}

function easeTo(options,fromQueue){
  var next=sanitizeCameraOptions(options);
  var payload={
    duration:numberValue(next.duration,450),
    essential:next.essential!==false
  };

  if(!map){return false;}
  if(!readyForCamera()&&!fromQueue){
    return queueCamera("ease",next);
  }

  if(next.center){payload.center=[next.center.lng,next.center.lat];}
  if(next.zoom!==undefined){payload.zoom=next.zoom;}
  if(next.bearing!==undefined){payload.bearing=next.bearing;}
  if(next.pitch!==undefined){payload.pitch=next.pitch;}
  if(next.offset){payload.offset=next.offset;}

  try{
    map.stop();
    map.easeTo(payload);
    return true;
  }catch(error){
    return false;
  }
}

function flyTo(options,fromQueue){
  var next=sanitizeCameraOptions(options);
  var config=mapConfig();

  if(!map||!next.center){return false;}
  if(!readyForCamera()&&!fromQueue){
    return queueCamera("fly",next);
  }

  try{
    map.stop();
    map.flyTo({
      center:[next.center.lng,next.center.lat],
      zoom:numberValue(next.zoom,config.resultZoom),
      bearing:numberValue(next.bearing,map.getBearing()),
      pitch:numberValue(next.pitch,map.getPitch()),
      duration:numberValue(next.duration,650),
      essential:next.essential!==false
    });
    return true;
  }catch(error){
    return false;
  }
}

function setCenter(center,options){
  var next=options||{};
  var valid=validCoordinate(center);

  if(!valid){return false;}

  if(next.immediate===true){
    return jumpTo({
      center:valid,
      zoom:next.zoom,
      bearing:next.bearing,
      pitch:next.pitch
    });
  }

  return easeTo({
    center:valid,
    zoom:next.zoom,
    bearing:next.bearing,
    pitch:next.pitch,
    duration:next.duration,
    offset:next.offset
  });
}

function fitResults(items,options,fromQueue){
  var list=Array.isArray(items)?items:[];
  var next=options||{};
  var coordinates=[];
  var bounds;
  var index;
  var config=mapConfig();

  if(!map){return false;}

  list.forEach(function(item){
    var coordinate=validCoordinate(item);
    if(coordinate){coordinates.push(coordinate);}
  });

  if(!coordinates.length){return false;}

  if(!readyForCamera()&&!fromQueue){
    pendingFit={
      items:list.slice(),
      options:clonePlain(next),
      generation:generation
    };
    return true;
  }

  if(coordinates.length===1){
    return setCenter(coordinates[0],{
      zoom:numberValue(next.zoom,config.placeZoom),
      duration:numberValue(next.duration,500)
    });
  }

  bounds=new window.mapboxgl.LngLatBounds();

  for(index=0;index<coordinates.length;index+=1){
    bounds.extend([
      coordinates[index].lng,
      coordinates[index].lat
    ]);
  }

  /*
    V600.6 Result Bounds Safety:
    One malformed or cross-market point must not zoom the map across a state
    or region. Use a stable average center instead of unsafe fitBounds.
  */
  try{
    var southWest=bounds.getSouthWest();
    var northEast=bounds.getNorthEast();
    var latSpan=Math.abs(
      Number(northEast.lat)-Number(southWest.lat)
    );
    var lngSpan=Math.abs(
      Number(northEast.lng)-Number(southWest.lng)
    );

    if(latSpan>1.35||lngSpan>1.75){
      var totalLat=0;
      var totalLng=0;

      coordinates.forEach(function(coordinate){
        totalLat+=Number(coordinate.lat);
        totalLng+=Number(coordinate.lng);
      });

      emit("map:warning",{
        code:"UNSAFE_RESULT_BOUNDS_REJECTED",
        count:coordinates.length,
        latSpan:latSpan,
        lngSpan:lngSpan
      });

      return setCenter({
        lat:totalLat/coordinates.length,
        lng:totalLng/coordinates.length
      },{
        zoom:numberValue(next.fallbackZoom,11.5),
        duration:numberValue(next.duration,450)
      });
    }
  }catch(boundsSafetyError){}

  try{
    map.stop();
    map.fitBounds(bounds,{
      padding:next.padding||config.fitPadding,
      maxZoom:numberValue(next.maxZoom,config.resultZoom),
      duration:numberValue(next.duration,650),
      essential:true
    });

    emit("map:results-fit",{
      count:coordinates.length
    });

    return true;
  }catch(error){
    return false;
  }
}

function focusPlace(item,options){
  var next=options||{};
  var config=mapConfig();
  var coordinate=validCoordinate(item);
  var selectionToken=String(
    next.selectionToken||
    item&&(
      item.placeId||
      item.id||
      item.googlePlaceId||
      item.name
    )||
    ""
  );

  if(!coordinate){return false;}

  setState("map.focus",{
    selectionToken:selectionToken,
    center:coordinate,
    committedAt:Date.now()
  },"map:focus-place");

  return easeTo({
    center:coordinate,
    zoom:numberValue(next.zoom,config.placeZoom),
    duration:numberValue(next.duration,500),
    offset:next.offset||[0,-80]
  });
}

function restoreSavedCamera(){
  var saved=stateGet("map.camera",null);
  var center=validCoordinate(saved&&saved.center);

  if(!saved||!center){return false;}

  return jumpTo({
    center:center,
    zoom:saved.zoom,
    bearing:saved.bearing,
    pitch:saved.pitch
  });
}

function restore(reason){
  var requestedReason=String(reason||"restore");
  var localGeneration=generation;

  if(!map){return false;}

  lastRestoreReason=requestedReason;

  if(restoreTimer){
    window.clearTimeout(restoreTimer);
  }

  restoreTimer=window.setTimeout(function(){
    restoreTimer=null;

    if(!map||localGeneration!==generation){return;}

    restoring=true;
    mergeState("map",{
      recovering:true
    },"map:restore-start");

    forceSurfaceVisible(requestedReason);
    applyInteraction("restore");
    resize("restore");
    restoreSavedCamera();

    scheduleSurfaceRecovery(
      requestedReason,
      {
        cameraMode:"restore-saved"
      }
    );

    window.requestAnimationFrame(function(){
      if(!map||localGeneration!==generation){return;}

      try{map.triggerRepaint();}catch(error){}

      flushPendingCamera();
      flushPendingFit();
      scheduleResize("restore-followup",90);

      restoring=false;
      mergeState("map",{
        recovering:false,
        loaded:mapLoaded(),
        styleLoaded:styleLoaded(),
        containerReady:true,
        surfaceVisible:surfaceLooksHealthy()
      },"map:restore-complete");

      emit("map:restored",{
        reason:lastRestoreReason,
        camera:cameraSnapshot(),
        healthy:surfaceLooksHealthy()
      });
    });
  },30);

  return true;
}

function destroy(){
  generation+=1;

  if(resizeTimer){
    window.clearTimeout(resizeTimer);
    resizeTimer=null;
  }

  if(restoreTimer){
    window.clearTimeout(restoreTimer);
    restoreTimer=null;
  }

  if(cameraTimer){
    window.clearTimeout(cameraTimer);
    cameraTimer=null;
  }

  clearRecoveryTimers();

  pendingCamera=null;
  pendingFit=null;
  restoring=false;

  if(map){
    try{map.remove();}catch(error){}
  }

  map=null;
  initialized=false;
  destroyed=true;
  visualStyleMode="wide";
  styleSwitchInFlight=false;
  try{if(document&&document.body){document.body.classList.remove("diq-adventure-satellite-mode");}}catch(error){}

  mergeState("map",{
    loaded:false,
    styleLoaded:false,
    interactionEnabled:false,
    containerReady:false,
    recovering:false,
    generation:generation
  },"map:destroy");

  emit("map:destroyed",{generation:generation});
  return true;
}


(function bindMapRecoveryEvents(){
  var bus=events();

  if(!bus||typeof bus.on!=="function"){
    return;
  }

  /*
    V600.6 Recovery Event Authority:
    These events are layout changes only. Repaint and resize the existing
    camera; never restore an older saved camera after scan result fitting.
  */
  [
    "scan:completed",
    "ui:map-results-shell-shown",
    "ui:mode-changed",
    "results:shelf-hidden",
    "results:shelf-minimized",
    "place:closed"
  ].forEach(function(eventName){
    try{
      bus.on(eventName,function(){
        scheduleSurfaceRecovery(
          eventName,
          {
            cameraMode:"surface-only"
          }
        );
      });
    }catch(error){}
  });

  /*
    Destination restore is a true camera restoration event.
  */
  try{
    bus.on("destination:restored",function(){
      scheduleSurfaceRecovery(
        "destination:restored",
        {
          cameraMode:"restore-saved"
        }
      );
    });
  }catch(error){}

  try{
    document.addEventListener(
      "visibilitychange",
      function(){
        if(document.visibilityState==="visible"){
          scheduleSurfaceRecovery(
            "document-visible",
            {
              cameraMode:"restore-saved"
            }
          );
        }
      },
      false
    );
  }catch(error){}

  try{
    window.addEventListener(
      "pageshow",
      function(){
        scheduleSurfaceRecovery(
          "pageshow",
          {
            cameraMode:"restore-saved"
          }
        );
      },
      false
    );

    window.addEventListener(
      "orientationchange",
      function(){
        scheduleSurfaceRecovery(
          "orientationchange",
          {
            cameraMode:"surface-only"
          }
        );
      },
      false
    );
  }catch(error){}
}());

window.DriveIQAdventureMap={
  init:init,
  getMap:getMap,
  isReady:isReady,
  enableInteraction:function(reason){
    return applyInteraction(reason||"manual");
  },
  disableInteraction:function(reason){
    return setInteraction(false,reason||"manual");
  },
  resize:resize,
  scheduleResize:scheduleResize,
  jumpTo:jumpTo,
  easeTo:easeTo,
  flyTo:flyTo,
  setCenter:setCenter,
  fitResults:fitResults,
  focusPlace:focusPlace,
  restore:restore,
  restoreSavedCamera:restoreSavedCamera,
  forceSurfaceVisible:forceSurfaceVisible,
  recoverSurface:recoverSurface,
  scheduleSurfaceRecovery:scheduleSurfaceRecovery,
  surfaceLooksHealthy:surfaceLooksHealthy,
  cameraSnapshot:cameraSnapshot,
  syncVisualStyle:syncVisualStyle,
  applyVisualStyleMode:applyVisualStyleMode,
  destroy:destroy,
  status:function(){
    return {
      build:"V600_6_CAMERA_RECOVERY_AUTHORITY",
      initialized:initialized,
      ready:isReady(),
      hasMap:!!map,
      destroyed:destroyed,
      generation:generation,
      restoring:restoring,
      lastRestoreReason:lastRestoreReason,
      lastCameraCommitAt:lastCameraCommitAt,
      lastHealthySurfaceAt:lastHealthySurfaceAt,
      surfaceHealthy:surfaceLooksHealthy(),
      hasPendingCamera:!!pendingCamera,
      hasPendingFit:!!pendingFit,
      visualStyleMode:visualStyleMode,
      styleSwitchInFlight:styleSwitchInFlight,
      satelliteEnterZoom:SATELLITE_ENTER_ZOOM,
      satelliteExitZoom:SATELLITE_EXIT_ZOOM
    };
  }
};

})(window,document);
